import React, { useEffect, useRef, useState } from 'react';
import { useWallet } from '../../context/WalletContext';
import { useContractState } from '../../context/ContractContext';
import { getADNFTContract } from '../../web3/contractInstance';
import { makeReadProvider } from '../../web3/providerHelper';
import { pickAvatar } from '../../utils/userAvatar';
import { formatUSDT, shortAddress } from '../../utils/format';
import InlineSpinner from '../loader/InlineSpinner';

const PAGE_SIZE = 10;

// Lazy loader for one level's members. Fetches IDs (paginated), then per-member
// wallet + totalInvestment + active via the users(uint256) getter.
export default function LevelMembers({ level }) {
  const { getEthersProvider } = useWallet();
  const { userId } = useContractState();
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const reqRef = useRef(0);

  useEffect(() => {
    if (!userId) return;
    const myReq = ++reqRef.current;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const ethersProvider = getEthersProvider();
        const provider = ethersProvider ? ethersProvider : makeReadProvider();
        const c = getADNFTContract(provider);

        const res = await c.getLevelUsers(userId, level, page, PAGE_SIZE);
        if (myReq !== reqRef.current) return;

        const ids = (res[0] || res).map(x => x.toNumber ? x.toNumber() : Number(x));
        const totalCount = res[1] && res[1].toNumber ? res[1].toNumber() : Number(res[1] || 0);
        setTotal(totalCount);

        const users = await Promise.all(
          ids.map(id => c.users(id).catch(() => null))
        );
        if (myReq !== reqRef.current) return;

        const shaped = ids.map((id, i) => {
          const u = users[i];
          if (!u) return { id, wallet: null, totalInvestment: 0, active: false };
          return {
            id,
            wallet: u.wallet || u[0],
            totalInvestment: u.totalInvestment || u[3],
            active: u.active !== undefined ? u.active : u[9]
          };
        });
        setMembers(shaped);
      } catch (e) {
        if (myReq === reqRef.current) setError('Failed to load members.');
      } finally {
        if (myReq === reqRef.current) setLoading(false);
      }
    })();
  }, [userId, level, page, getEthersProvider]);

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  if (loading && members.length === 0) {
    return (
      <div className="level-expand">
        <div className="empty" style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'center' }}>
          <InlineSpinner /> Loading members…
        </div>
      </div>
    );
  }
  if (error) {
    return <div className="level-expand"><div className="empty">{error}</div></div>;
  }
  if (total === 0) {
    return <div className="level-expand"><div className="empty">No members at this level yet.</div></div>;
  }

  return (
    <div className="level-expand">
      {members.map(m => (
        <div key={m.id} className="member-row">
          <div className="avatar"><img src={pickAvatar(m.wallet)} alt="" /></div>
          <div className="info">
            <div className="id">ID #{m.id} {m.active
              ? <span className="badge success" style={{ marginLeft: 6 }}>Active</span>
              : <span className="badge mute" style={{ marginLeft: 6 }}>Inactive</span>}
            </div>
            <div className="addr">{shortAddress(m.wallet)}</div>
          </div>
          <div className="inv">
            <div className="a">{formatUSDT(m.totalInvestment)}</div>
            <div className="s">invested</div>
          </div>
        </div>
      ))}

      {total > PAGE_SIZE && (
        <div className="level-pager">
          <button className="btn btn-ghost btn-sm" disabled={page === 0 || loading}
                  onClick={() => setPage(p => Math.max(0, p - 1))}>← Prev</button>
          <span>Page {page + 1} of {pageCount} · {total} total</span>
          <button className="btn btn-ghost btn-sm" disabled={page >= pageCount - 1 || loading}
                  onClick={() => setPage(p => Math.min(pageCount - 1, p + 1))}>Next →</button>
        </div>
      )}
    </div>
  );
}
